import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { ComponentProps, ReactElement, useEffect, useEffectEvent, useState } from 'react';
import { useIntl } from 'react-intl';
import { useGlobalSearchPeekQuery } from 'store/api/globalSearch';
import { GlobalSearchEntityType } from 'types/globalSearch';
import { captureSearchPeekOpened, SearchSurface } from 'utils/searchAnalytics';
import { GlobalSearchPeekCard, GlobalSearchPeekSkeleton } from './GlobalSearchPeekCard';

/** How long the pointer rests on a row before it is asking for the card rather than passing over it. */
const OPEN_DELAY_MS = 220;

/** Long enough to cross the gap between the row or link and the card without it closing behind the pointer. */
export const CLOSE_DELAY_MS = 160;

/** The gap the design leaves between the row and the card. */
const CARD_OFFSET_PX = 12;

/** Lifts the card's top edge slightly above the row's, as the design draws it. */
const CARD_ALIGN_OFFSET_PX = -6;

type CardPlacement = Pick<ComponentProps<typeof PopoverContent>, 'side' | 'align'>;

export type RowWithPeekProps = CardPlacement & {
    /**
     * The row to hang the card off, already built by the caller — a plain result row, the best-bet
     * row, or the sheet's own. It is rendered as the card's trigger, so it has to pass the props it
     * does not recognise through to the element it draws, which every search row does.
     */
    row: ReactElement;
    entityType: GlobalSearchEntityType;
    /** The record's id within its family. */
    recordId: number;
    /** Which surface the row is on. Reported with the analytics event, nothing else. */
    surface: SearchSurface;
    /**
     * Whether this is the one row the panel has the pointer on. The card is drawn only there, which
     * is what makes two cards at once impossible rather than merely unlikely: the panel moves this
     * onto a row the instant the pointer reaches it, so the card the pointer left goes with it.
     */
    hovered: boolean;
};

/**
 * A record row with the hover peek attached.
 *
 * The card is mounted by this wrapper and not by the row, so the rows themselves stay free of it:
 * they are drawn on three surfaces, only two of them peek, and a row that knew about the card could
 * not be rendered or tested without one. Whoever draws a row that should peek wraps it here.
 *
 * The pointer resting here is what raises the card, after the delay below — and the panel's single
 * hovered row is what keeps it up. Every row owns its own hover timers, so two of them can be in
 * flight at once; only one row is the hovered one, so only one card is ever on the screen.
 *
 * Only records get a card. Pages and actions describe no record, and the results page draws the
 * same figures in the row itself.
 */
export function RowWithPeek({ row, entityType, recordId, surface, hovered, side, align }: RowWithPeekProps) {
    const [open, setOpen] = useState(false);
    /*
     * A record the caller may not open answers 404, exactly as a record that does not exist does —
     * the endpoint makes the two indistinguishable on purpose. So a failure means there is no card,
     * which is nothing to tell the user about: the row stays and nothing opens. Held here rather
     * than read from the query so that the row stops claiming to have a card open.
     */
    const [unavailable, setUnavailable] = useState(false);
    const showCard = open && !unavailable && hovered;

    return (
        <Popover
            open={showCard}
            onOpenChange={(next, details) => {
                // Clicking a row opens the record. It must never also raise a card over the list
                // the click is leaving.
                if (details.reason === 'trigger-press') {
                    return;
                }
                // Reported for the card that is drawn, and only that one: the selection moving
                // between rows does not touch the hover state this reads. `unavailable` is written
                // by the card's own read, which has not run at a row's first open — so a record
                // the caller may not open is still reported once, and suppressed on every hover
                // after that.
                if (next && !open && hovered && !unavailable) {
                    captureSearchPeekOpened({ surface, entityType });
                }
                setOpen(next);
            }}
        >
            <PopoverTrigger
                openOnHover
                delay={OPEN_DELAY_MS}
                closeDelay={CLOSE_DELAY_MS}
                nativeButton={false}
                render={row}
            />
            {/*
             * Mounted only while the card is wanted. A panel draws dozens of rows and the read is
             * one record's, so subscribing every row to a query it will almost certainly never run
             * would cost the whole list to serve the one row the pointer stops on.
             */}
            {showCard && (
                <PeekPopup
                    entityType={entityType}
                    recordId={recordId}
                    side={side}
                    align={align}
                    onUnavailable={() => setUnavailable(true)}
                />
            )}
        </Popover>
    );
}

/**
 * The card's own fetch and the popup it fills, alive only while the row or link it hangs off is being
 * hovered. It has to sit inside a `Popover`; without a trigger there, `anchor` is what it stands beside.
 */
export function PeekPopup({
    entityType,
    recordId,
    side = 'inline-end',
    align = 'start',
    anchor,
    onUnavailable,
}: CardPlacement &
    Pick<ComponentProps<typeof PopoverContent>, 'anchor'> & {
        entityType: GlobalSearchEntityType;
        recordId: number;
        /** Called once the read has failed: there is no card for this record. */
        onUnavailable: () => void;
    }) {
    const intl = useIntl();
    // Per-argument caching is RTK Query's, not ours: a pointer crossing ten rows starts no request
    // for the nine it only passed over, and coming back to one already read opens it at once and
    // reads it again behind it, so a record changed a moment ago does not keep its old status.
    // `currentData`, not `data`: the best-bet row keeps this mounted when its record changes, and `data`
    // would hold the previous record's card until the new one lands.
    const { currentData, isError } = useGlobalSearchPeekQuery(
        { entityType, id: recordId },
        { refetchOnMountOrArgChange: true }
    );
    const reportUnavailable = useEffectEvent(onUnavailable);

    useEffect(() => {
        if (isError) {
            reportUnavailable();
        }
    }, [isError]);

    return (
        <PopoverContent
            anchor={anchor}
            side={side}
            align={align}
            sideOffset={CARD_OFFSET_PX}
            // The lift is for a card standing beside its row; centred over the narrow sheet the
            // same offset would only push it off centre.
            alignOffset={align === 'center' ? 0 : CARD_ALIGN_OFFSET_PX}
            // The card follows the pointer, never the focus: taking the focus would close the
            // search box the pointer left, and handing it back would fight the keyboard.
            initialFocus={false}
            finalFocus={false}
            role="dialog"
            aria-label={intl.formatMessage({ id: 'global-search.peek.label' })}
            // The panel under the search box keeps the box focused by swallowing its own mousedown;
            // the card is portalled out of that panel, so it has to do the same or pressing one of
            // its buttons would blur the box and unmount the card mid-click.
            onMouseDown={(event) => event.preventDefault()}
            // Marks the card, so a host listening on the whole document can tell a pointer on it from one elsewhere.
            data-peek-card
            positionerClassName="z-(--z-tooltip)"
            // A card taller than the room beside its row scrolls inside itself rather than running off the screen.
            className="bg-paper shadow-peek z-(--z-tooltip) max-h-(--available-height) w-80 gap-0 overflow-y-auto overscroll-contain rounded-[10px] p-0 ring-0"
        >
            {/*
             * The skeleton draws the card's own shape, and the card is anchored by its top edge, so
             * what arrives grows downwards instead of moving under the pointer.
             */}
            {currentData ? <GlobalSearchPeekCard card={currentData} /> : <GlobalSearchPeekSkeleton />}
        </PopoverContent>
    );
}
